import { httpClient } from "@bw/shared/api";
import type { AuditQueryResponse } from "@bw/shared/types";
import type { components } from "@bw/shared/types/generated";

export type SettlementSummaryResponse =
  components["schemas"]["SettlementSummaryResponse"];
export type SettlementPeriodResponse =
  components["schemas"]["SettlementPeriodResponse"];
export type PayoutHistoryResponse =
  components["schemas"]["PayoutHistoryResponse"];
export type PayoutLedgerEntryResponse =
  components["schemas"]["PayoutLedgerEntryResponse"];
export type PlatformFinanceSummaryResponse =
  components["schemas"]["PlatformFinanceSummaryResponse"];

export async function getFinanceSummary(): Promise<AuditQueryResponse> {
  return httpClient.get<AuditQueryResponse>(
    "/audit?resource_type=transaction&limit=100",
  );
}

/**
 * Headline platform finance KPIs (GET /platform/finance/summary, platform_admin
 * only): Pending Payouts (amount + count), Commission MTD (the platform's
 * commission earnings), Revenue MTD (gross transaction volume / GMV), the next
 * platform payout date, and the calendar-month window. The money fields are
 * `null` on an empty / mixed-currency platform — the UI renders an em-dash, NEVER
 * a fabricated $0. httpClient prefixes /bff/api, so we pass a backend-space path.
 */
export async function getPlatformFinanceSummary(): Promise<PlatformFinanceSummaryResponse> {
  return httpClient.get<PlatformFinanceSummaryResponse>(
    "/platform/finance/summary",
  );
}

/**
 * Platform settlement summary: per-period money rollups plus totals across the
 * whole platform (GET /platform/finance/settlements, platform_admin only).
 * For the platform view, total_platform_fees is the platform's revenue cut and
 * total_net_owed is what is owed out to facilities. httpClient prefixes
 * /bff/api, so we pass a backend-space path.
 */
export async function getPlatformSettlements(
  periods = 6,
): Promise<SettlementSummaryResponse> {
  return httpClient.get<SettlementSummaryResponse>(
    `/platform/finance/settlements?periods=${periods}`,
  );
}

/**
 * Platform payout history: actual Stripe payouts across the platform, optionally
 * scoped to one org (GET /platform/finance/payouts?org_id=&status=&limit=&offset=,
 * platform_admin only). Distinct from the settlement summary above (accrued
 * amounts by period) — this is the disbursement record. Each entry's `amount` is
 * a SIGNED Money; the component renders every field VERBATIM (no client-side
 * money math). httpClient prefixes /bff/api, so we pass a backend-space path.
 */
export async function getPlatformPayouts(opts?: {
  orgId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<PayoutHistoryResponse> {
  const params = new URLSearchParams();
  if (opts?.orgId) params.set("org_id", opts.orgId);
  if (opts?.status) params.set("status", opts.status);
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.offset != null) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return httpClient.get<PayoutHistoryResponse>(
    `/platform/finance/payouts${qs ? `?${qs}` : ""}`,
  );
}
